import { toolbarButtonsLite } from 'core/utils/froalaConfig';
import type React from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

type FroalaExtraConfig = Readonly<{
  quickInsertEnabled: boolean;
  tabSpaces: false;
  toolbarButtons: typeof toolbarButtonsLite;
  toolbarButtonsMD: typeof toolbarButtonsLite;
  toolbarButtonsSM: typeof toolbarButtonsLite;
  toolbarButtonsXS: typeof toolbarButtonsLite;
}>;

type UseDialogRichTextInputParams = Readonly<{
  buttonTextCancel?: string;
  buttonTextSuccess?: string;
  editorMinHeight?: string;
  hasQuickInsert?: boolean;
  minLength?: number;
  onCancel?: () => void;
  onClose: () => void;
  placeholder?: string;
  value: string;
}>;

type UseDialogRichTextInputResult = Readonly<{
  editorStyle: React.CSSProperties | undefined;
  extraConfig: FroalaExtraConfig;
  isDisabledSuccess: boolean;
  resolvedButtonTextCancel: string;
  resolvedButtonTextSuccess: string;
  resolvedOnCancel: () => void;
  resolvedPlaceholder: string;
}>;

function useDialogRichTextInput({
  buttonTextCancel,
  buttonTextSuccess,
  editorMinHeight,
  hasQuickInsert = false,
  minLength = 0,
  onCancel,
  onClose,
  placeholder,
  value,
}: UseDialogRichTextInputParams): UseDialogRichTextInputResult {
  const { t } = useTranslation(['actions', 'placeholder']);

  const resolvedButtonTextCancel = buttonTextCancel ?? t('actions:Cancel');
  const resolvedButtonTextSuccess = buttonTextSuccess ?? t('actions:Save');
  const resolvedPlaceholder = placeholder ?? t('placeholder:Write here');
  // Si el consumidor no provee onCancel, reutilizamos onClose como fallback
  // seguro para que el boton Cancel siempre tenga una accion valida (mismo
  // patron que DialogConfirm).
  const resolvedOnCancel = onCancel ?? onClose;

  const isDisabledSuccess = value.length < minLength;

  const editorStyle = editorMinHeight
    ? ({ '--ui-dialog-rich-text-input-editor-min-height': editorMinHeight } as React.CSSProperties)
    : undefined;

  // `tabSpaces: false` desactiva la captura de Tab dentro del editor Froala
  // para que el foco pueda salir hacia los botones del dialog (Cancel/Save).
  // Cumple WCAG 2.1.2 "No Keyboard Trap": en un modal, Tab debe permitir
  // recorrer todos los elementos focusables sin quedar atrapado en el editor.
  const extraConfig = useMemo<FroalaExtraConfig>(
    () => ({
      quickInsertEnabled: hasQuickInsert,
      tabSpaces: false,
      toolbarButtons: toolbarButtonsLite,
      toolbarButtonsMD: toolbarButtonsLite,
      toolbarButtonsSM: toolbarButtonsLite,
      toolbarButtonsXS: toolbarButtonsLite,
    }),
    [hasQuickInsert],
  );

  return {
    editorStyle,
    extraConfig,
    isDisabledSuccess,
    resolvedButtonTextCancel,
    resolvedButtonTextSuccess,
    resolvedOnCancel,
    resolvedPlaceholder,
  };
}

export default useDialogRichTextInput;
